import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Vendors from './pages/Vendors';
import Projects from './pages/Projects';
import APInvoices from './pages/APInvoices';
import APInvoiceForm from './pages/APInvoiceForm';
import APInvoiceDetail from './pages/APInvoiceDetail';
import ARInvoices from './pages/ARInvoices';
import ARInvoiceForm from './pages/ARInvoiceForm';
import ARInvoiceDetail from './pages/ARInvoiceDetail';
import CostCenters from './pages/CostCenters';
import ChartOfAccounts from './pages/ChartOfAccounts';
import FXRates from './pages/FXRates';
import Users from './pages/Users';
import Companies from './pages/Companies';
import Employees from './pages/Employees';
import EmployeeDetail from './pages/EmployeeDetail';
import Settings from './pages/Settings';
import AuditLogs from './pages/AuditLogs';
import APInvoicesDashboard from './pages/APInvoicesDashboard';
import Banks from './pages/Banks';
import CustomerManagement from './pages/CustomerManagement';
import ChannelReport from './pages/ChannelReport';
import ChannelReportDetail from './pages/ChannelReportDetail';
import ChannelManagement from './pages/ChannelManagement';
import MonthlyStatistics from './pages/MonthlyStatistics';
import MyProfile from './pages/MyProfile';
import DirectReports from './pages/DirectReports';
import ChangePassword from './pages/ChangePassword';
import './styles/index.css';

const PrivateRoute = ({ children, permission, requiredRole }) => {
  const { user, loading, hasPermission } = useAuth();

  if (loading) {
    return (
      <div className="loading-overlay" style={{ minHeight: '100vh' }}>
        <div className="spinner" />
        <span>Loading...</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check role if specified
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/" />;
  }

  // Check permission if specified
  if (permission && !hasPermission(permission)) {
    return <Navigate to="/" />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  return (
    <div className="app-container">
      <Navbar />
      <main className="main-content">{children}</main>
    </div>
  );
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/vendors"
        element={
          <PrivateRoute permission="vendors_view">
            <AppLayout>
              <Vendors />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <PrivateRoute permission="projects_view">
            <AppLayout>
              <Projects />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <PrivateRoute permission="invoices_view">
            <AppLayout>
              <APInvoices />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices/new"
        element={
          <PrivateRoute permission="invoices_edit">
            <AppLayout>
              <APInvoiceForm />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <PrivateRoute permission="invoices_view">
            <AppLayout>
              <APInvoiceDetail />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices/:id/edit"
        element={
          <PrivateRoute permission="invoices_edit">
            <AppLayout>
              <APInvoiceForm />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/ar-invoices"
        element={
          <PrivateRoute permission="ar_invoices_view">
            <AppLayout>
              <ARInvoices />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/ar-invoices/new"
        element={
          <PrivateRoute permission="ar_invoices_edit">
            <AppLayout>
              <ARInvoiceForm />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/ar-invoices/:id"
        element={
          <PrivateRoute permission="ar_invoices_view">
            <AppLayout>
              <ARInvoiceDetail />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/ar-invoices/:id/edit"
        element={
          <PrivateRoute permission="ar_invoices_edit">
            <AppLayout>
              <ARInvoiceForm />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/customers/management"
        element={
          <PrivateRoute permission="customers_view">
            <AppLayout>
              <CustomerManagement />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/customers/channel-report"
        element={
          <PrivateRoute permission="channels_view">
            <AppLayout>
              <ChannelReport />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/customers/channel-report/:id"
        element={
          <PrivateRoute permission="channels_view">
            <AppLayout>
              <ChannelReportDetail />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/customers/channel-management"
        element={
          <PrivateRoute permission="channels_view">
            <AppLayout>
              <ChannelManagement />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/customers/monthly-statistics"
        element={
          <PrivateRoute permission="customers_view">
            <AppLayout>
              <MonthlyStatistics />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/dashboards/ap-invoices"
        element={
          <PrivateRoute permission="invoices_view">
            <AppLayout>
              <APInvoicesDashboard />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/finance/cost-centers"
        element={
          <PrivateRoute permission="invoices_view">
            <AppLayout>
              <CostCenters />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/finance/chart-of-accounts"
        element={
          <PrivateRoute permission="invoices_view">
            <AppLayout>
              <ChartOfAccounts />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/finance/fx-rates"
        element={
          <PrivateRoute permission="invoices_view">
            <AppLayout>
              <FXRates />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/finance/banks"
        element={
          <PrivateRoute permission="invoices_view">
            <AppLayout>
              <Banks />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <PrivateRoute permission="companies_view">
            <AppLayout>
              <Companies />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute permission="users_manage">
            <AppLayout>
              <Users />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <PrivateRoute permission="employees_view">
            <AppLayout>
              <Employees />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/employees/:id"
        element={
          <PrivateRoute permission="employees_view">
            <AppLayout>
              <EmployeeDetail />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <PrivateRoute requiredRole="admin">
            <AppLayout>
              <AuditLogs />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute permission="users_manage">
            <AppLayout>
              <Settings />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/my-profile"
        element={
          <PrivateRoute>
            <AppLayout>
              <MyProfile />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/direct-reports"
        element={
          <PrivateRoute>
            <AppLayout>
              <DirectReports />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/change-password"
        element={
          <PrivateRoute>
            <AppLayout>
              <ChangePassword />
            </AppLayout>
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1a2027',
              color: '#f1f5f9',
              border: '1px solid #334155',
            },
            success: {
              iconTheme: { primary: '#10b981', secondary: '#f1f5f9' },
            },
            error: {
              iconTheme: { primary: '#ef4444', secondary: '#f1f5f9' },
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
